import type { CSSProperties, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  color?: string;
  border?: string;
  onClick?: () => void;
  small?: boolean;
  padding?: number | string;
  className?: string;
  style?: CSSProperties;
  testId?: string;
  label?: string;
  /** The bubble holds its own buttons, so it is clickable as a div (a button cannot nest buttons). */
  containsControls?: boolean;
}

/** A rounded, softly shadowed, brightly coloured container (the building block of every screen). */
export function Bubble({ children, color, border, onClick, small, padding, className = '', style, testId, label, containsControls }: Props) {
  const s: CSSProperties = { ...(color ? { background: color } : {}), ...(border ? { border } : {}), ...(padding != null ? { padding } : {}), ...style };
  const cls = `bubble${small ? ' small' : ''} ${className}`.trim();
  if (onClick && containsControls) {
    return (
      <div
        role="button"
        tabIndex={0}
        className={`${cls} clickable`}
        style={s}
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            onClick();
          }
        }}
        data-testid={testId}
        aria-label={label}
      >
        {children}
      </div>
    );
  }
  if (onClick) {
    return (
      <button type="button" className={cls} style={s} onClick={onClick} data-testid={testId} aria-label={label}>
        {children}
      </button>
    );
  }
  return (
    <div className={cls} style={s} data-testid={testId}>
      {children}
    </div>
  );
}
